import { createTranslator } from 'short-uuid'
import { validate } from 'uuid'

/**
 * Central short UUID utility for encoding/decoding UUIDs in URLs.
 *
 * This provides a consistent way to shorten UUIDs for cleaner, more user-friendly URLs
 * while maintaining full UUID compatibility internally and in the database.
 *
 * Example:
 * UUID:     550e8400-e29b-41d4-a716-446655440000 (36 chars)
 * Short ID: 2h4jfk8l3m9n1p5q7r0s6t4u              (~25 chars, lowercase alphanumeric)
 *
 * ## Usage in TanStack Router Routes
 *
 * ```typescript
 * import { decodeId, encodeId } from '@/lib/short-uuid'
 *
 * // For routes with single param (e.g., /org/$orgId)
 * export const Route = createFileRoute('/org/$orgId')({
 *   params: {
 *     parse: (rawParams) => ({
 *       orgId: decodeId(rawParams.orgId)
 *     }),
 *     stringify: (params) => ({
 *       orgId: encodeId(params.orgId)
 *     })
 *   },
 *   loader: ({ params }) => {
 *     // params.orgId is now a full UUID
 *   }
 * })
 *
 * // For routes with multiple params (e.g., /org/$orgId/workspace/$workspaceId)
 * export const Route = createFileRoute('/org/$orgId/workspace/$workspaceId')({
 *   params: {
 *     parse: (rawParams) => ({
 *       orgId: decodeId(rawParams.orgId),
 *       workspaceId: decodeId(rawParams.workspaceId)
 *     }),
 *     stringify: (params) => ({
 *       orgId: encodeId(params.orgId),
 *       workspaceId: encodeId(params.workspaceId)
 *     })
 *   }
 * })
 * ```
 */

// Custom lowercase alphanumeric alphabet for cleaner, URL-friendly short IDs
const translator = createTranslator('0123456789abcdefghijklmnopqrstuvwxyz')

/**
 * Encodes a UUID to short ID. Non-UUIDs pass through unchanged.
 *
 * This defensive behavior is intentional - TanStack Router calls stringify
 * with raw URL values before parse runs during route building.
 *
 * @param uuid - Standard UUID string (with or without dashes)
 * @returns Short ID string (~25 chars, lowercase alphanumeric), or input unchanged if not a UUID
 *
 * @example
 * ```ts
 * encodeId('550e8400-e29b-41d4-a716-446655440000')
 * // Returns: '2h4jfk8l3m9n1p5q7r0s6t4u'
 *
 * encodeId('invalid-input')
 * // Returns: 'invalid-input' (unchanged)
 * ```
 */
export function encodeId(uuid: string): string {
  // Only encode valid UUIDs - pass through anything else unchanged
  if (!validate(uuid)) {
    return uuid
  }
  return translator.fromUUID(uuid)
}

/**
 * Decodes a short ID back to a full UUID.
 *
 * Handles both short IDs and UUIDs for resilience:
 * - If input is already a UUID, returns it unchanged
 * - If input is a short ID, decodes it to UUID
 *
 * @param shortId - Short ID string from URL or UUID
 * @returns Standard UUID string (lowercase, with dashes)
 * @throws Error if input is neither a valid short ID nor UUID
 *
 * @example
 * ```ts
 * decodeId('2h4jfk8l3m9n1p5q7r0s6t4u')
 * // Returns: '550e8400-e29b-41d4-a716-446655440000'
 *
 * decodeId('550e8400-e29b-41d4-a716-446655440000')
 * // Returns: '550e8400-e29b-41d4-a716-446655440000' (unchanged)
 * ```
 */
export function decodeId(shortId: string): string {
  // If it's already a UUID, return it unchanged
  if (validate(shortId)) {
    return shortId.toLowerCase()
  }

  // Validate before decoding
  if (!translator.validate(shortId)) {
    throw new Error(`Invalid ID format: ${shortId}`)
  }

  return translator.toUUID(shortId)
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Whether a string is a full, hyphenated UUID. */
export function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value)
}
